import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/apiClient';
import { authService } from '@/services/auth/authService';

/**
 * Wire types of GET /api/finance/budgetsetup/status (BudgetSetupStatusDto tree). Status is derived
 * live from the data on every request — nothing is persisted, a COMPLETE step can regress.
 * spec: docs/15-designspecs/Setup-Flows.md (Phase 1).
 */
export type SetupStepStatus = 'BLOCKED' | 'NOT_STARTED' | 'INCOMPLETE' | 'COMPLETE' | 'WARNING';
export type SetupPhaseRepetition = 'ONE_TIME' | 'PER_INSTANCE';

export interface SetupStepLink {
  label: string;
  route: string;
}

export interface SetupStepQuickAction {
  commandId: string;
  label: string;
  sourceVersionId: string;
  sourceVersionLabel: string;
}

export interface SetupStepResult {
  stepId: string;
  title: string;
  description: string;
  status: SetupStepStatus;
  advisory: boolean;
  reason: string;
  route: string;
  /** Localized name of the target view — the "Open …" button names where you land. */
  routeLabel: string;
  /** Full button text overriding the default "Open {routeLabel}" (e.g. "Create version"). */
  actionLabel: string | null;
  /** Form pre-fill passed as navigation state — a "create" action opens with context chosen. */
  routePreFill: Record<string, string> | null;
  actualCount: number;
  requiredCount: number;
  dependsOn: string[];
  evaluationMs: number;
  /** Fix links: the exact records the reason talks about (e.g. each broken plan view). */
  links: SetupStepLink[];
  /** Optional quick-create action (e.g. CopyPlanVersion on the cycle-start step). */
  quickAction: SetupStepQuickAction | null;
}

export interface SetupInstance {
  instanceId: string;
  label: string;
  parentInstanceId: string | null;
  /** Localized instance facts (scenarios: type · fiscal year · date range). */
  info: string | null;
  steps: SetupStepResult[];
}

export interface SetupPhaseQuickAction {
  commandId: string;
  label: string;
  /** The resolved source scenario — typically the one that just left the filter (last year's). */
  sourceScenarioId: string;
  sourceScenarioLabel: string;
}

export interface SetupPhase {
  id: string;
  title: string;
  order: number;
  repetition: SetupPhaseRepetition;
  instanceEntity: string | null;
  emptyStateRoute: string | null;
  steps: SetupStepResult[];
  instances: SetupInstance[];
  /** Zero-instance empty state only (e.g. CopyPlanScenario on cycleStart — the new-year moment). */
  emptyStateQuickAction: SetupPhaseQuickAction | null;
}

export interface SetupFlowSummary {
  completedUnits: number;
  totalUnits: number;
  worstStepId: string | null;
  worstStepTitle: string | null;
  worstInstanceLabel: string | null;
  worstReason: string | null;
  nextActionStepId: string | null;
  nextActionTitle: string | null;
  nextActionRoute: string | null;
  nextActionInstanceLabel: string | null;
  /** Instance id of the next action — labels are not unique, so "start here" matches on this. */
  nextActionInstanceId: string | null;
}

export interface BudgetSetupStatus {
  flowName: string;
  title: string;
  description: string;
  summary: SetupFlowSummary;
  phases: SetupPhase[];
  evaluatedAtUtc: string;
  totalEvaluationMs: number;
}

export const budgetSetupStatusKey = (tenantId?: string | null, legalEntityId?: string | null) =>
  ['budget-setup-status', tenantId, legalEntityId] as const;

/**
 * The BudgetSetup hub's single read. No client cache beyond the query default — the page is a live
 * health check; the toolbar's refresh triggers refetch ("Utvärdera igen"). The evaluation is scoped
 * to the current legal entity (the apiClient sends X-Nexo-Legal-Entity-Id), so the LE must be part
 * of the query key — switching LE re-evaluates.
 */
export function useBudgetSetupStatus() {
  const tenantId = authService.getTenantId();
  const legalEntityId = authService.getLegalEntityId();

  return useQuery<BudgetSetupStatus, Error>({
    queryKey: budgetSetupStatusKey(tenantId, legalEntityId),
    queryFn: async () => {
      const response = await apiClient.get<BudgetSetupStatus>('/api/finance/budgetsetup/status');
      return response.data;
    },
  });
}
